/**
 * Factory that assembles the concrete CoreAPI on the client.
 *
 * Phase 1: the pure subsystems (events / i18n / rbac) are real and tested;
 * the Firebase-backed subsystems are typed skeletons that throw until
 * Phase 2 implements them against the emulator. `createCoreAPI` already
 * returns a valid, fully-typed CoreAPI object so apps/web can boot.
 *
 * In dev/test it points at the Firebase emulators; in prod it wires to
 * praesto-dev / -staging / -prod via environment variables.
 */
import type { CoreAPI } from "@praesto/contracts";
import { createAuthService } from "./auth";
import { createTenantService } from "./tenant";
import { createOrganizationService } from "./organization";
import { CORE_PERMISSIONS, createRbacService } from "./rbac";
import { createNotificationService } from "./notifications";
import { createI18nService } from "./i18n";
import { createEventBus } from "./events";
import { createUsageService } from "./usage";
import { createStorageService } from "./storage";
import { initFirebase } from "./internal/firestore";
import { createRuntimeContext } from "./internal/context";

export interface CoreProviderConfig {
  readonly tenantSlug: string; // first URL segment, e.g. 'blackfin'
  readonly firebaseConfig: {
    readonly apiKey: string;
    readonly authDomain: string;
    readonly projectId: string;
    readonly appId: string;
    readonly storageBucket: string;
    readonly messagingSenderId: string; // required by FCM web push
  };
  readonly emulators?: {
    readonly auth?: string; // 'http://127.0.0.1:9099'
    readonly firestore?: string; // '127.0.0.1:8080'
    readonly functions?: string; // '127.0.0.1:5001'
    readonly storage?: string; // '127.0.0.1:9199'
  };
}

export function createCoreAPI(config: CoreProviderConfig): CoreAPI {
  const firebase = initFirebase(
    config.firebaseConfig,
    config.emulators
      ? {
          ...(config.emulators.auth ? { auth: config.emulators.auth } : {}),
          ...(config.emulators.firestore ? { firestore: config.emulators.firestore } : {}),
          ...(config.emulators.storage ? { storage: config.emulators.storage } : {}),
        }
      : undefined,
  );

  // Pin the Identity Platform tenant BEFORE Firebase Auth tries to rehydrate
  // the cached session from IndexedDB. Without this, `auth.tenantId === null`
  // at boot, so the cached session (whose user has `tenantId: slug`) is
  // rejected as cross-tenant and the user gets bounced back to login on
  // every page reload.
  firebase.auth.tenantId = config.tenantSlug;

  // Phase 1 skeleton: the tenant slug is the tenant anchor. Phase 2 replaces
  // this with the authenticated user's verified `tenantId` claim.
  const ctx = createRuntimeContext(config.tenantSlug, firebase);

  const core: CoreAPI = {
    auth: createAuthService(ctx),
    tenant: createTenantService(ctx),
    organization: createOrganizationService(ctx),
    rbac: createRbacService({ memberships: [], permissions: CORE_PERMISSIONS }),
    notifications: createNotificationService(ctx),
    i18n: createI18nService(),
    events: createEventBus(),
    usage: createUsageService(ctx),
    storage: createStorageService(ctx),
  };

  // Internal escape hatch for the React hooks (useFirestoreSubscription).
  // Not part of the public CoreAPI contract — modules must use the typed
  // services for everything except live snapshots. The underscore is the
  // marker.
  (core as unknown as { _firebase: typeof firebase })._firebase = firebase;

  return Object.freeze(core);
}
